import { useState, useCallback, useRef, useEffect } from "react";

const MIN_WIDTH = 80;
const MAX_WIDTH = 500;

interface ColumnWidths {
  [key: string]: number;
}

interface UseColumnResizeOptions {
  tableId: string;
  defaultWidths: ColumnWidths;
}

export function useColumnResize({ tableId, defaultWidths }: UseColumnResizeOptions) {
  const storageKey = `table-widths-${tableId}`;

  const [widths, setWidths] = useState<ColumnWidths>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...defaultWidths, ...parsed };
      }
    } catch {}
    return { ...defaultWidths };
  });

  const dragState = useRef<{
    column: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  const persist = useCallback(
    (next: ColumnWidths) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {}
    },
    [storageKey]
  );

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragState.current) return;
      const { column, startX, startWidth } = dragState.current;
      const delta = e.clientX - startX;
      const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + delta));
      setWidths((prev) => {
        const updated = { ...prev, [column]: next };
        return updated;
      });
    },
    []
  );

  const onMouseUp = useCallback(() => {
    if (!dragState.current) return;
    dragState.current = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    setWidths((prev) => {
      persist(prev);
      return prev;
    });
  }, [persist]);

  useEffect(() => {
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  const startResize = useCallback(
    (column: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragState.current = {
        column,
        startX: e.clientX,
        startWidth: widths[column] || defaultWidths[column] || 150,
      };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [widths, defaultWidths]
  );

  const resetWidths = useCallback(() => {
    setWidths({ ...defaultWidths });
    try {
      localStorage.removeItem(storageKey);
    } catch {}
  }, [defaultWidths, storageKey]);

  const isDragging = dragState.current !== null;
  const draggingColumn = dragState.current?.column ?? null;

  return { widths, startResize, resetWidths, isDragging, draggingColumn };
}
